import {
	useCallback,
	useState,
	useEffect,
	useMemo,
	SetStateAction,
} from 'react';
import { useMutation, useQuery } from 'react-query';
import { GenericModalProps } from 'components/genericModal';
import {
	actionFetchSessionDetail,
	actionUpdateSession,
	actionInsertSession,
	SessionValues,
	SessionInsertValue,
	SessionUpdateValue,
	actionCloseSession,
} from 'pages/sessionManagement/actions';
import { useNavigate } from 'react-router-dom';
import { actionFetchTimezones } from 'components/timezones/redux';
import { useAppDispatch, useAppSelector } from 'hooks';
import { actionResetSessionIds } from 'components/clientSessionSelect/sessionSelect/redux';
import { add } from 'date-fns';
import { toZonedTime } from 'date-fns-tz';

//Custom types used within the model
export type InputValue<Type> = {
	value?: Type;
	options?: Type[];
	set?: (a: Type) => void;
};
export type SelectValue = {
	value?: string;
	options: { [key: string]: string };
	set?: (key: string) => void;
};
export type BooleanIndicator = {
	value: boolean;
	set: (newValue: boolean) => void;
};
export enum NewSessionMode {
	Optiweigh = 'optiweigh',
	Upload = 'upload',
}

//Model definition (interface)
export type SessionDetailsModel = {
	session_id?: number;
	isLoading: boolean;
	errorMsg?: string;
	autoCloseSessions?: string[];
	modalMessage?: GenericModalProps;

	onSubmit?: (fileUploadAction?: Function) => void;
	onCancel: () => void;

	//Common for all sessions types
	isNewSession: boolean;
	isYardWeights: boolean;
	name: InputValue<string>;
	timezone: InputValue<string>;
	startTime: InputValue<Date>;
	endTime?: InputValue<Date | null>;
	breed?: InputValue<string>;
	sex?: SelectValue;
	age?: InputValue<string>;
	headCount?: InputValue<string>;
	woolGrowth?: SelectValue;
	paddockSize?: InputValue<string>;
	adjustType?: SelectValue;
	adjustFactor?: InputValue<string>;
	adjustFactorUnits?: string;
	displayUnits: SelectValue;
	UseNoIDs: SelectValue;
	newSessionCreated: boolean;
	minWeight?: InputValue<string>;
	maxWeight?: InputValue<string>;

	currentTime?: InputValue<boolean>;

	//For file upload
	speciesSelect?: SelectValue;
	musterTime?: InputValue<number | null>;
	feedType?: InputValue<string>;
	uploadFile?: File;
	setUploadFile?: (f: File) => void;

	//Weighers
	weighers: { id: number; name: string; allowed: boolean; checked: boolean }[];
	setWeigher?: (id: number, selected: boolean) => void;
};

//Utility functions used by the model instance
const initialise_species_options = (
	species: string | null,
	breed: InputValue<string>,
	sex: SelectValue,
	age: InputValue<string>
) => {
	if (species === 'cattle') {
		breed.options = [
			'British',
			'Euro',
			'British x Euro',
			'Bos Indicus',
			'British x Bos Indicus',
			'Euro x Bos Indicus',
			'Composite',
			'Dairy',
			'Wagyu/Wagyu X',
			'Mixed',
		];
		sex.options = {
			M: 'Bulls',
			N: 'Steers',
			F: 'Heifers / Cows',
			X: 'Mixed',
		};
		age.options = [
			'Calves',
			'Weaners',
			'Yearlings',
			'Young Adults',
			'Mature Adults',
			'Cows and calves',
			'Mixed',
		];
	} else if (species === 'sheep') {
		sex.options = {
			M: 'Rams',
			N: 'Wethers',
			F: 'Ewes',
			X: 'Mixed',
		};
		age.options = ['0.5', '1', '2', '3+'];
	}
};

const noUseIDsToEidTagsString = (noUseIDs: number): string => {
	return noUseIDs ? 'no' : 'yes';
};
const eidTagsStringToNoUseIDs = (eidString: string): number => {
	return eidString === 'yes' ? 0 : 1;
};
const factorToString = (factor: number | null, type: number): string => {
	if (!factor) return '';
	if (type === 4) return (100 * factor).toFixed(1);
	return (100 * (1 - factor)).toFixed(1);
};
const factorFromString = (str: string, type: number): number | null => {
	if (type === 0) return null;
	if (type === 4) return Number(str) / 100;
	return (100 - Number(str)) / 100;
};
const validate_adjusted_weight = (
	type: number,
	factor: number | null
): string | null => {
	console.log(`validating ${factor} type ${type}`);
	if (type < 1) return null;
	if (!factor) return 'Weight adjustment amount is invalid';
	if (type === 4) {
		if (factor < 0.4 || factor > 0.6)
			return 'Carcase weight adjustment should be between 40% and 60%';
	} else if (type === 3) {
		if (factor < 0.75 || factor > 1.0)
			return 'Empty curfew weight adjustment should be between -25% and 0%';
	} else if (type === 2 || type === 1) {
		if (factor < 0.75 || factor > 1.1)
			return 'Farm yards weight adjustment should be between -25% and +10%';
	} else {
		return 'Unknown weight adjustment mode';
	}
	return null;
};

//Function that returns an instance of the model (factory)
export const useSessionDetailsModel = (
	session_id?: number,
	mode?: NewSessionMode
): SessionDetailsModel => {
	const session_list = useAppSelector((state) => state.sessionIds);
	const client_id = useAppSelector((state) => state.clientId);
	const currentUser = useAppSelector((state) => state.currentUser);
	const weighers = useAppSelector((state) => state.weighers);
	const timezone_list = useAppSelector((state) => state.timezones);
	const [timezones_loaded, set_timezones_loaded] = useState(false);
	const has_api_token = currentUser.username.length > 3;
	const new_session = !session_id;

	const [state_session_id, setState_session_id] = useState<number | undefined>(
		undefined
	);
	const [name_value, setName] = useState('');
	const [timezone_value, setTimezone] = useState<string | null>(null);
	const [start_value, setStart] = useState(new Date());
	const [current_time_value, setcurrenttime] = useState(true);
	const [end_value, setEnd] = useState<Date | null>(null);
	const [weigher_selection, set_weigher_selection] = useState<number[]>([]);
	const [breed_value, setBreed] = useState<string | null>(null);
	const [sex_value, setSex] = useState<string | null>(null);
	const [age_value, setAge] = useState<string | null>(null);
	const [units_value, setUnits] = useState('m');
	const [head_value, setHead] = useState('');
	const [paddock_value, setPaddock] = useState('');
	const [wool_value, setWool] = useState<string | null>(null);
	const [adjust_type_value, setAdjustType] = useState('');
	const [adjust_factor_value, setAdjustFactor] = useState('');
	const [species_value, setSpecies] = useState('cattle');
	const [musterTime_value, setMusterTime] = useState<number | null>(null);
	const [feedType_value, setFeedType] = useState<string>('');
	const [eid_value, setEid] = useState('yes');
	const [min_weight_value, setMinWeight] = useState<string>('');
	const [max_weight_value, setMaxWeight] = useState<string>('');
	const [upload_file, setUploadFile] = useState<File | null>(null);

	const [user_cancel, setUserCancel] = useState(false); // has the user clicked cancel
	const [errorMsg, setErrorMsg] = useState<string | null>();
	const [newSessionCreated, setNewSessionCreated] = useState<boolean>(false);

	const dispatch = useAppDispatch();
	const navigate = useNavigate();
	const leave = useCallback(() => {
		navigate('/manage/session');
	}, [navigate]);

	const fetch = useQuery(
		['session', session_id],
		() => actionFetchSessionDetail(session_id!, client_id!),
		{ enabled: has_api_token && !!client_id && !!session_id }
	);
	const post = useMutation({
		mutationFn: async (session: SessionValues) => {
			if (new_session) {
				const close = add(session.sessionStart, { seconds: -1 });
				for (const c of sessions_to_close) {
					await actionCloseSession(c.SessionID, close, session.timezone);
				}
				const s: SessionInsertValue = {
					Client: client_id!,
					...session,
				};
				return await actionInsertSession(s);
			} else {
				const s: SessionUpdateValue = { ...session, SessionID: session_id };
				await actionUpdateSession(s);
			}
		},
		onSuccess: (response) => {
			dispatch(actionResetSessionIds());
		},
		onError: (err) => {
			if (err instanceof Error) setErrorMsg(err.message);
		},
	});

	const eidTags: SelectValue = {
		value: eid_value,
		options: { yes: 'Yes', no: 'No' },
		set: setEid,
	};
	const displayUnits: SelectValue = {
		value: units_value,
		options: { m: 'Metric (kg)', i: 'Imperial (lb)' },
		set: setUnits,
	};
	const name: InputValue<string> = {
		value: name_value,
		set: setName,
	};
	const timezone: InputValue<string> = {
		value: timezone_value ?? undefined,
		set: setTimezone,
		options: timezone_list,
	};

	const currentTime: InputValue<boolean> = {
		value: current_time_value,
		set: setcurrenttime,
	};
	const startTime: InputValue<Date> = {
		value: start_value,
	};
	const breed: InputValue<string> = {
		value: breed_value ?? undefined,
		set: setBreed,
	};
	const sex: SelectValue = {
		value: sex_value ?? undefined,
		set: setSex,
		options: {},
	};
	const age: InputValue<string> = {
		value: age_value ?? undefined,
		set: setAge,
	};
	const headCount: InputValue<string> = {
		value: head_value ?? undefined,
		set: setHead,
	};
	const paddockSize: InputValue<string> = {
		value: paddock_value ?? undefined,
		set: setPaddock,
	};

	const adjustType: SelectValue = {
		value: adjust_type_value || '0',
		set: setAdjustType,
		options: {
			0: 'Do not show',
			1: 'Farm Yards - Full',
			2: 'Farm Yards - Empty',
			3: 'Empty Curfew (Feedlot/saleyard)',
			4: 'Carcase',
		},
	};
	const adjustFactor: InputValue<string> = {
		value: adjust_factor_value,
		set: setAdjustFactor,
	};
	const minWeight: InputValue<string> = {
		value: min_weight_value,
		set: setMinWeight,
	};
	const maxWeight: InputValue<string> = {
		value: max_weight_value,
		set: setMaxWeight,
	};
	const speciesSelect: SelectValue = {
		value: species_value,
		set: setSpecies,
		options: { cattle: 'Cattle', sheep: 'Sheep' },
	};
	const musterTime: InputValue<number | null> = {
		value: musterTime_value ?? undefined,
		set: setMusterTime,
	};
	const feedType: InputValue<string> = {
		value: feedType_value ?? undefined,
		set: setFeedType,
		options: [
			'Dry pasture',
			'Green/dry pasture',
			'Lush green pasture',
			'Feedlot rations',
		],
	};

	// CALLBACKS/HOOKS
	// fetch timezone list
	useEffect(() => {
		if (timezone_list?.length) set_timezones_loaded(true);
		else if (!timezones_loaded) {
			dispatch(
				actionFetchTimezones((loading: boolean) => {
					set_timezones_loaded(!loading);
				})
			);
		}
	}, [timezones_loaded, timezone_list, dispatch]);
	// load current values for edit
	useEffect(() => {
		if (fetch.data) {
			setName(fetch.data.SessionName);
			setStart(fetch.data.sessionStartObj);
			setEnd(fetch.data.sessionEndObj);
			set_weigher_selection(fetch.data.weighers);
			setTimezone(fetch.data.timezone);
			setHead(fetch.data.Head?.toString() ?? '');
			setBreed(fetch.data.BreedType);
			setSex(fetch.data.Sex);
			setAge(fetch.data.Age);
			setWool(fetch.data.WoolGrowth);
			setPaddock(fetch.data.Paddock?.toString() ?? '');
			setAdjustType(fetch.data.adjustType?.toString() ?? '');
			setAdjustFactor(
				factorToString(fetch.data.adjustFactor, fetch.data.adjustType ?? 0)
			);
			setUnits(fetch.data.displayUnits);
			setMusterTime(
				fetch.data.musterTime ? Number(fetch.data.musterTime) : null
			);
			setFeedType(fetch.data.feedType ?? '');
			setEid(noUseIDsToEidTagsString(fetch.data.UseNoIDs));
			setMinWeight(fetch.data.minWeight?.toString() ?? '');
			setMaxWeight(fetch.data.maxWeight?.toString() ?? '');
			setState_session_id(fetch.data.SessionID);
		}
	}, [fetch.data]);
	// initialise start time to now for new sessions
	useEffect(() => {
		if (new_session && current_time_value && timezone_value) {
			setStart(toZonedTime(new Date(), timezone_value));
		}
	}, [new_session, current_time_value, timezone_value]);
	// auto select weigher if only one
	useEffect(() => {
		if (weighers?.length === 1) set_weigher_selection([weighers[0].WeigherID]);
	}, [weighers]);
	// preselect values based on last session for selected weigher
	useEffect(() => {
		if (!new_session) return;
		if (weigher_selection.length < 1) return;
		if (!session_list) return;
		const s = session_list
			.filter((s) => s.weighers.includes(weigher_selection[0]))
			.sort((a, b) => b.sessionStart.getTime() - a.sessionStart.getTime());
		if (s.length > 0) {
			setTimezone(s[0].timezone);
			setUnits(s[0].displayUnits);
			setAdjustType(s[0].adjustType?.toString() ?? '');
			setAdjustFactor(
				factorToString(s[0].adjustFactor ?? null, s[0].adjustType ?? 0)
			);
		}
	}, [new_session, weigher_selection, session_list]);
	// open sessions warning based on weigher selection
	const sessions_to_close = useMemo(() => {
		if (!new_session) return [];
		if (!session_list) return [];
		if (mode === NewSessionMode.Upload) return [];
		const open_sessions = session_list.filter((s) => {
			return s.isOpen && s.weighers.some((w) => weigher_selection.includes(w));
		});
		if (open_sessions.length === 0) return [];
		return open_sessions;
	}, [new_session, session_list, weigher_selection, mode]);
	// session species determined by weigher selection or species selection
	const species = useMemo(() => {
		if (mode === NewSessionMode.Upload) return species_value;
		if (fetch.data) return fetch.data.species;
		if (!new_session) return null;
		if (weigher_selection.length < 1) return null;
		if (!weighers) return null;
		const w = weighers.filter((w) => w.WeigherID === weigher_selection[0]);
		return w[0]?.Species.toLowerCase();
	}, [
		fetch.data,
		new_session,
		weigher_selection,
		weighers,
		species_value,
		mode,
	]);
	// For weight upload scenario, set the session end date to the same as session start
	useEffect(() => {
		if (mode === NewSessionMode.Upload) {
			setEnd(start_value);
		}
	}, [start_value, mode]);

	const is_yard_weights = useMemo(() => {
		return (
			(!new_session && fetch.data?.isYardWeights) ||
			mode === NewSessionMode.Upload
		);
	}, [new_session, fetch.data, mode]);

	const weigherChecked = useCallback(
		(id: number, checked: boolean) => {
			if (!weighers) return null;
			const w = weighers.filter((w) => w.WeigherID === id);
			if (w.length !== 1) return;
			if (species != null && w[0].Species.toLowerCase() !== species) return;
			if (checked) set_weigher_selection([...weigher_selection, id]);
			else set_weigher_selection(weigher_selection.filter((w) => w !== id));
		},
		[weigher_selection, species, weighers]
	);
	const any_changes = useMemo(() => {
		let changesDetected = false;

		if (new_session) {
			changesDetected =
				name_value.length > 0 ||
				weigher_selection.length > 0 ||
				breed_value != null ||
				sex_value != null ||
				age_value != null ||
				head_value !== '' ||
				paddock_value !== '' ||
				wool_value != null ||
				adjust_type_value !== '' ||
				adjust_factor_value !== '' ||
				eid_value !== 'yes' ||
				min_weight_value !== '' ||
				max_weight_value !== '' ||
				musterTime_value !== null ||
				feedType_value !== '';
		}

		if (fetch.data) {
			changesDetected =
				name_value !== fetch.data.SessionName ||
				timezone_value !== fetch.data.timezone ||
				start_value.getTime() !== fetch.data.sessionStartObj.getTime() ||
				end_value?.getTime() !== fetch.data.sessionEndObj?.getTime() ||
				(fetch.data.Head && Number(head_value) !== fetch.data.Head) ||
				breed_value !== fetch.data.BreedType ||
				sex_value !== fetch.data.Sex ||
				age_value !== fetch.data.Age ||
				wool_value !== fetch.data.WoolGrowth ||
				(fetch.data.Paddock && Number(paddock_value) !== fetch.data.Paddock) ||
				units_value !== fetch.data.displayUnits ||
				adjust_type_value !== (fetch.data.adjustType?.toString() ?? '') ||
				adjust_factor_value !==
					factorToString(fetch.data.adjustFactor, fetch.data.adjustType ?? 0) ||
				eid_value !== noUseIDsToEidTagsString(fetch.data.UseNoIDs) ||
				min_weight_value !== (fetch.data.minWeight?.toString() ?? '') ||
				max_weight_value !== (fetch.data.maxWeight?.toString() ?? '') ||
				musterTime_value !== fetch.data.musterTime ||
				feedType_value !== (fetch.data.feedType ?? '');
		}

		return changesDetected;
	}, [
		name_value,
		weigher_selection,
		timezone_value,
		start_value,
		end_value,
		breed_value,
		sex_value,
		age_value,
		head_value,
		paddock_value,
		wool_value,
		units_value,
		adjust_type_value,
		adjust_factor_value,
		fetch.data,
		new_session,
		eid_value,
		musterTime_value,
		feedType_value,
		min_weight_value,
		max_weight_value,
	]);
	const onCancel = useCallback(() => {
		if (any_changes) {
			setUserCancel(true);
		} else {
			leave();
		}
	}, [any_changes, leave]);

	//onSubmit actions, including validation
	const onSubmit = useCallback(
		(fileUploadAction?: Function) => {
			const adjust_factor = factorFromString(
				adjust_factor_value,
				Number(adjust_type_value)
			);
			const validate = (): string | null => {
				//Validation common to both Optiweigh sessions and yard upload sessions
				if (name_value == null || name_value.length === 0)
					return `Enter a 'Session name'`;
				if (timezone_value == null || timezone_value === '')
					return "Select 'Timezone'";
				if (age_value == null || age_value === '') return "Select 'Age'";

				//Validation specific to Optiweigh sessions (as opposed to uploaded session weights)
				if (mode === NewSessionMode.Optiweigh) {
					if (breed_value == null || breed_value === '')
						return "Select 'Breed'";
					if (sex_value == null || sex_value === '') return "Select 'Sex'";
					if (new_session && weigher_selection.length === 0)
						return `Select one or more weighers`;
					if (
						paddock_value == null ||
						paddock_value === '' ||
						isNaN(Number(paddock_value))
					)
						return "Enter 'Paddock size' estimate";
					if (
						head_value == null ||
						head_value === '' ||
						isNaN(Number(head_value))
					)
						return "Enter 'Number of head' estimate";
				}
				if (isNaN(Number(min_weight_value)))
					return "Enter a valid 'Minimum weight'";
				if (isNaN(Number(max_weight_value)))
					return "Enter a valid 'Maxiumum weight'";
				if (
					Number(max_weight_value) > 0 &&
					Number(min_weight_value) >= Number(max_weight_value)
				)
					return 'Ensure Minimum weight is less than than Maximum weight';

				//Add any validation specifically required for yard weights upload form here
				if (mode === NewSessionMode.Upload) {
					if (musterTime_value == null) return 'Add the muster time';
					if (feedType_value == null || feedType_value === '')
						return 'Add the feed type';
					if (!upload_file && new_session)
						return 'Select weights file for upload'; //Only an error if no file for new sessions. Existing sessions can be edited without needing a new file
				}
				return null;
			};
			let err = validate();
			if (!err && adjust_factor)
				err = validate_adjusted_weight(
					Number(adjust_type_value),
					adjust_factor
				);
			setErrorMsg(err);
			if (err) return;

			//Validation complete. Ready to send the data
			const adjustType = Number(adjust_type_value);

			const session: SessionValues = {
				//The data to be sent
				SessionName: name_value,
				sessionStart: start_value,
				...((end_value || fetch.data?.sessionEndObj) && {
					sessionEnd: end_value,
				}),
				timezone: timezone_value!,
				Sex: sex_value!,
				Age: age_value!,
				BreedType: breed_value!,
				displayUnits: units_value === 'i' ? 'i' : 'm',
				UseNoIDs: eidTagsStringToNoUseIDs(eid_value),
				adjustType,
				adjustFactor: factorFromString(adjust_factor_value, adjustType)!,
				...(wool_value && { WoolGrowth: wool_value }),
				...(!is_yard_weights && {
					Head: Number(head_value),
					Paddock: Number(paddock_value),
					...(weigher_selection.length > 0 && { weighers: weigher_selection }),
					minWeight: min_weight_value === '' ? null : Number(min_weight_value),
					maxWeight: max_weight_value === '' ? null : Number(max_weight_value),
				}),
				...(musterTime_value && { musterTime: Number(musterTime_value) }),
				...(feedType_value && { feedType: feedType_value }),
				...(is_yard_weights && {
					isYardWeights: is_yard_weights,
				}),
			};

			//Perform the update
			post.mutate(session, {
				onSuccess(responseData, variables, context) {
					if (mode === NewSessionMode.Upload && !session_id) {
						//For new file upload sessions, get the newly created sessionId to use for the file name
						console.log('New session created OK');
						const newSessionId = responseData.insertId;
						console.debug(
							`Need to now upload file for new session ${newSessionId}`
						);
						if (fileUploadAction) fileUploadAction(newSessionId);
						setNewSessionCreated(true);
						setState_session_id(newSessionId);
					} else {
						leave();
					}
				},
				onError(error, variables, context) {
					console.error(`Error creating session`);
					console.error(error);
				},
			});
		},
		[
			name_value,
			weigher_selection,
			timezone_value,
			start_value,
			end_value,
			breed_value,
			sex_value,
			age_value,
			head_value,
			paddock_value,
			wool_value,
			units_value,
			adjust_type_value,
			adjust_factor_value,
			fetch.data,
			new_session,
			is_yard_weights,
			post,
			musterTime_value,
			feedType_value,
			upload_file,
			eid_value,
			min_weight_value,
			max_weight_value,
			mode,
			leave,
			session_id,
		]
	);

	const is_carcase = useMemo(
		() => Number(adjust_type_value) === 4,
		[adjust_type_value]
	);
	const adjustFactorUnits = useMemo(
		() => (is_carcase ? '% of paddock weight' : '% discount on paddock weight'),
		[is_carcase]
	);

	//INIT MODEL AND SET PROPERTY VALUES
	const model: SessionDetailsModel = {
		isLoading: fetch.isLoading || post.isLoading || !timezones_loaded,
		...(errorMsg && { errorMsg: errorMsg }),
		...(sessions_to_close.length > 0 && {
			autoCloseSessions: sessions_to_close.map(
				(s) => `Session ${s.SessionID} ${s.SessionName}`
			),
		}),
		onCancel,
		isNewSession: new_session,
		isYardWeights: is_yard_weights,
		name,
		timezone,
		startTime,
		displayUnits,
		adjustFactorUnits,
		weighers: [],
		UseNoIDs: eidTags,
		newSessionCreated,
		session_id: state_session_id,
	};

	if (species != null) {
		initialise_species_options(species, breed, sex, age);
		model.breed = breed;
		model.sex = sex;
		model.age = age;
	}

	model.startTime.set = setStart;

	if (!is_yard_weights) {
		if (end_value !== null || fetch.data?.sessionEndObj) {
			model.endTime = {
				...(end_value && { value: end_value }),
				set: setEnd,
			};
		}

		model.headCount = headCount;
		model.paddockSize = paddockSize;
		model.adjustType = adjustType;
		model.minWeight = minWeight;
		model.maxWeight = maxWeight;
		if (Number(adjustType.value) > 0) model.adjustFactor = adjustFactor;
		if (weighers) {
			model.weighers = weighers.map((w) => {
				return {
					id: w.WeigherID,
					name: w.name,
					allowed: species == null || w.Species.toLowerCase() === species,
					checked: weigher_selection.includes(w.WeigherID),
				};
			});
		}

		if (new_session) {
			model.currentTime = currentTime;
			model.setWeigher = weigherChecked;
		} else model.weighers = model.weighers.filter((w) => w.checked);
	}

	if (species === 'sheep') {
		model.woolGrowth = {
			value: wool_value ?? undefined,
			set: setWool,
			options: Object.fromEntries(
				Array(13)
					.fill(0)
					.map((v: any, i: number) => [i, `${i} month${i === 1 ? '' : 's'}`])
			),
		};
	}

	if (any_changes) {
		model.onSubmit = onSubmit;
	}

	if (user_cancel) {
		model.modalMessage = {
			title: 'Unsaved changes',
			text: 'Your changes to this session have not be saved, are you sure you want to exit?',
			show: user_cancel,
			onClose() {
				setUserCancel(false);
			},
			buttons: {
				exit: {
					label: 'Exit without saving changes',
					onClick: leave,
				},
				stay: { label: 'Cancel', onClick: () => setUserCancel(false) },
			},
		};
	}
	if (newSessionCreated) {
		model.modalMessage = {
			title: '? Session created',
			text: `Session #${model.session_id} has been created with the uploaded weights.`,
			show: true,
			buttons: {
				exit: {
					label: 'OK',
					onClick: leave,
				},
			},
		};
	}

	//If this session details model is being used for weight upload component, add the necessary properties to it.
	if (mode === NewSessionMode.Upload) {
		model.speciesSelect = speciesSelect;
		model.musterTime = musterTime;
		model.feedType = feedType;
		model.adjustType = adjustType;
		model.timezone = timezone;
		model.uploadFile = upload_file ?? undefined;
		model.setUploadFile = (f: File) => {
			console.log('file dropped ', JSON.stringify(f));
			if (!client_id || upload_file != null) return;
			setUploadFile(f);
		};
	}

	//Determine which inputs are restricted from being set when editing a session
	if (!new_session) {
		delete model.speciesSelect?.set;
		delete model.breed?.set;
		delete model.sex?.set;
		delete model.setUploadFile;
	}

	return model;
};
